// The allocation grid (PE4–PE6): assignments × weeks on Glide Data Grid, the canvas grid P0 chose
// for its native fill handle and smooth scrolling across 150+ columns.
//   - Typing, paste (to and from Excel) and the fill handle all arrive as one batch of edits.
//   - Month and quarter zoom show average hours a week; editing one sets every week in it.
//   - Under the assignments, one load row per named person colors each period by §3.9, with the
//     percentage written in the cell so it reads without color. Hovering lists what makes it up.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CompactSelection,
  DataEditor,
  GridCellKind,
  type EditListItem,
  type GridCell,
  type GridColumn,
  type GridMouseEventArgs,
  type DataEditorRef,
  type GridSelection,
  type Item,
  type TextCell,
  type Theme,
} from "@glideapps/glide-data-grid";
import "@glideapps/glide-data-grid/dist/index.css";
import { addDays, periodsOf, rateOn, statusOf, today, type CostBasis, type Estimate, type LoadThresholds, type RowRates, type WorkingPlan, type Zoom } from "../../../core";
import type { LoadResponse } from "../api";
import { MOCK } from "../config";
import { cents, money, pct } from "../format";

// P0 finding: warm Glide's lazily loaded editors so the first keystrokes of a session aren't dropped.
void import("glide-preload/number-editor");
void import("glide-preload/overlay-editor");

export interface GridEdit {
  row: number;
  weeks: number[];
  value: number;
}

export interface Block {
  rows: number[];
  first: number;
  last: number;
}

const ROW_HEIGHT = 30;
const HEADER_HEIGHT = 30;
const GROUP_HEIGHT = 26;

const BAR = "rgba(136, 177, 201, 0.32)"; // SKY wash: a non-zero run reads as a bar
const LOAD_THEME = {
  over: { bgCell: "#941100", textDark: "#FFFFFF" }, // DAWN
  on: { bgCell: "#007F7F", textDark: "#FFFFFF" }, // SEA
  under: { bgCell: "#88B1C9", textDark: "#2B3C4D" }, // SKY fill, DARK text for contrast
} as const;

const theme: Partial<Theme> = {
  fontFamily: "'Nunito Sans', 'Avenir Next LT Pro', 'Segoe UI', system-ui, sans-serif",
  baseFontStyle: "13px",
  headerFontStyle: "600 12px",
  editorFontSize: "13px",
  textDark: "#2B3C4D",
  textMedium: "#46576F",
  textLight: "#8C97A8",
  textHeader: "#2B3C4D",
  textGroupHeader: "#46576F",
  accentColor: "#46576F",
  accentLight: "rgba(136, 177, 201, 0.25)",
  bgHeader: "#E7E6E6",
  bgHeaderHasFocus: "#D9D8D8",
  bgHeaderHovered: "#DEDDDD",
  bgCell: "#FFFFFF",
  borderColor: "rgba(43, 60, 77, 0.12)",
  horizontalBorderColor: "rgba(43, 60, 77, 0.08)",
};
const totalTheme: Partial<Theme> = { bgCell: "#F3F2F2", baseFontStyle: "600 13px" };
const linkTheme: Partial<Theme> = { textDark: "#007F7F" }; // SEA: a name that opens the person's card
const loadLabelTheme: Partial<Theme> = { bgCell: "#FAFAFA", textDark: "#46576F" };

const num1 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });

export interface AllocationGridProps {
  plan: WorkingPlan;
  zoom: Zoom;
  roleName: (id: string) => string;
  personName: (id: string | null) => string;
  /** Present when the caller may see money; the rate and cost columns appear only then. */
  rates: (RowRates | undefined)[] | null;
  basis: CostBasis;
  estimate: Estimate | null;
  load: LoadResponse | null;
  thresholds: LoadThresholds;
  editable: boolean;
  onEdits: (edits: GridEdit[]) => void;
  onSelect: (block: Block | null) => void;
  onOpenRow: (row: number) => void;
  /** Present when the caller may open people's cards: named people's cells link to them (rev. 1.6). */
  onOpenPerson?: (personId: string) => void;
}

export function AllocationGrid(props: AllocationGridProps) {
  const { plan, zoom, estimate, load, thresholds, editable, rates, basis } = props;
  const frozenWeeks = plan.frozenBefore ? Math.max(0, Math.round((Date.parse(plan.frozenBefore) - Date.parse(plan.startMonday)) / 604_800_000)) : 0;
  // Callbacks and name lookups are read through a ref, so a parent re-render that changes nothing
  // the grid draws (a selection, a dialog) doesn't rebuild getCellContent. Glide rebuilds its focus
  // target when that changes, and the focused cell disappearing swallowed the next keystroke.
  const latest = useRef(props);
  latest.current = props;
  const periods = useMemo(() => periodsOf(plan, zoom), [plan.startMonday, plan.weeks, zoom]); // eslint-disable-line react-hooks/exhaustive-deps
  const financial = !!rates;
  const fixed = useMemo<GridColumn[]>(
    () => [
      { title: "Role", id: "role", width: 140 },
      { title: "Person", id: "person", width: 150 },
      ...(financial ? [{ title: "Rate", id: "rate", width: 78 }] : []),
      { title: "Hours", id: "hours", width: 66 },
      ...(financial ? [{ title: "Cost", id: "cost", width: 90 }] : []),
    ],
    [financial],
  );
  const FROZEN = fixed.length;
  const columns = useMemo<GridColumn[]>(() => {
    const width = zoom === "week" ? 46 : zoom === "month" ? 66 : 76;
    return [
      ...fixed,
      ...periods.map<GridColumn>((p) => ({
        title: p.label,
        id: p.key,
        width,
        group: zoom === "week" ? addDays(p.key, 3).slice(0, 7) : zoom === "month" ? p.key.slice(0, 4) : undefined,
      })),
    ];
  }, [fixed, periods, zoom]);

  const n = plan.assignments.length;
  const loadPeople = useMemo(() => [...new Set(plan.assignments.map((a) => a.personId).filter((p): p is string => !!p))], [plan.assignments]);
  const rows = n + 1 + loadPeople.length;

  // This plan's hours per person per week, then load per period.
  const loadCells = useMemo(() => {
    const out = new Map<string, { pct: number | null; hours: number; other: number; capacity: number }[]>();
    for (const person of loadPeople) {
      const mine = new Array(plan.weeks).fill(0);
      for (const a of plan.assignments) if (a.personId === person) a.hours.forEach((h, w) => (mine[w] += h));
      const weeks = load?.people[person];
      out.set(
        person,
        periods.map((p) => {
          let hours = 0;
          let other = 0;
          let capacity = 0;
          for (const w of p.weeks) {
            hours += mine[w];
            other += weeks?.other[w] ?? 0;
            capacity += weeks?.capacity[w] ?? 0;
          }
          const total = hours + other;
          return { pct: !weeks || total === 0 ? null : capacity > 0 ? total / capacity : Infinity, hours, other, capacity };
        }),
      );
    }
    return out;
  }, [loadPeople, plan, periods, load]);

  const referenceDay = useMemo(() => {
    const t = today();
    return t >= plan.startMonday && t <= addDays(plan.startMonday, plan.weeks * 7) ? t : plan.startMonday;
  }, [plan.startMonday, plan.weeks]);

  const periodValue = useCallback((hours: number[], weeks: number[]) => weeks.reduce((s, w) => s + (hours[w] ?? 0), 0) / weeks.length, []);

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const text = (value: string, align: "left" | "right" = "left", themeOverride?: Partial<Theme>): TextCell => ({
        kind: GridCellKind.Text,
        data: value,
        displayData: value,
        allowOverlay: false,
        readonly: true,
        contentAlign: align,
        themeOverride,
      });
      const name = (personId: string | null, base?: Partial<Theme>): GridCell =>
        personId && latest.current.onOpenPerson
          ? { ...text(latest.current.personName(personId), "left", { ...base, ...linkTheme }), cursor: "pointer", hoverEffect: true }
          : text(latest.current.personName(personId), "left", base);
      const fixedId = fixed[col]?.id;

      if (row === n) {
        if (fixedId === "role") return text("Total", "left", totalTheme);
        if (fixedId === "hours") return text(estimate ? num1.format(estimate.hours) : "", "right", totalTheme);
        if (fixedId === "cost") return text(estimate ? money(estimate.labor) : "", "right", totalTheme);
        if (col < FROZEN) return text("", "left", totalTheme);
        const p = periods[col - FROZEN];
        const total = plan.assignments.reduce((s, a) => s + periodValue(a.hours, p.weeks), 0);
        return text(total ? num1.format(total) : "", "right", totalTheme);
      }

      if (row > n) {
        const person = loadPeople[row - n - 1];
        if (fixedId === "role") return text("Load", "left", loadLabelTheme);
        if (fixedId === "person") return name(person, loadLabelTheme);
        if (col < FROZEN) return text("", "left", loadLabelTheme);
        const cell = loadCells.get(person)?.[col - FROZEN];
        if (!cell || cell.pct === null) return text("", "right");
        const label = cell.pct === Infinity ? "no cap." : cell.pct > 9.99 ? ">999%" : pct(cell.pct, 0);
        return text(label, "right", LOAD_THEME[cell.pct === Infinity ? "over" : statusOf(cell.pct, thresholds)]);
      }

      const a = plan.assignments[row];
      if (fixedId === "role") return text(latest.current.roleName(a.roleId));
      if (fixedId === "person") return name(a.personId);
      if (fixedId === "rate") {
        // The rate when this row's work starts, so a row costing $0 (no pay on file yet) shows it.
        const steps = rates?.[row]?.[basis];
        const first = a.hours.findIndex((h) => h > 0);
        const on = first >= 0 ? addDays(plan.startMonday, first * 7 + 2) : referenceDay;
        return text(steps ? cents(rateOn(steps, on)) : "—", "right");
      }
      if (fixedId === "hours") return text(estimate ? num1.format(estimate.rowHours[row] ?? 0) : "", "right");
      if (fixedId === "cost") return text(estimate ? money(estimate.rowCost[row] ?? 0) : "", "right");

      const p = periods[col - FROZEN];
      const value = periodValue(a.hours, p.weeks);
      // After a resnap, weeks before frozenBefore are the hours reported: shown, not editable (PP3).
      const actual = frozenWeeks > 0 && p.weeks.every((w) => w < frozenWeeks);
      return {
        kind: GridCellKind.Number,
        data: value,
        displayData: value ? num1.format(value) : "",
        allowOverlay: editable && !actual,
        readonly: !editable || actual,
        contentAlign: "right",
        allowNegative: false,
        themeOverride: actual ? { bgCell: "#EEF0F3", textDark: "#46576F" } : value ? { bgCell: BAR } : undefined,
      };
    },
    [fixed, FROZEN, n, estimate, periods, plan, loadPeople, loadCells, rates, basis, referenceDay, editable, thresholds, periodValue, frozenWeeks],
  );

  const onCellsEdited = useCallback(
    (edits: readonly EditListItem[]) => {
      const out: GridEdit[] = [];
      for (const { location: [col, row], value } of edits) {
        if (col < FROZEN || row >= n || value.kind !== GridCellKind.Number) continue;
        const v = value.data ?? 0;
        const weeks = periods[col - FROZEN].weeks.filter((w) => w >= frozenWeeks);
        if (weeks.length) out.push({ row, weeks, value: Number.isFinite(v) ? v : 0 });
      }
      if (out.length) latest.current.onEdits(out);
      return true;
    },
    [FROZEN, n, periods, frozenWeeks],
  );

  const [selection, setSelection] = useState<GridSelection>({ columns: CompactSelection.empty(), rows: CompactSelection.empty() });
  const onSelectionChange = useCallback(
    (s: GridSelection) => {
      setSelection(s);
      const r = s.current?.range;
      if (!r || r.x + r.width <= FROZEN || r.y >= n) return latest.current.onSelect(null);
      const firstCol = Math.max(FROZEN, r.x);
      const lastCol = r.x + r.width - 1;
      const rows = Array.from({ length: r.height }, (_, i) => r.y + i).filter((x) => x < n);
      latest.current.onSelect({ rows, first: periods[firstCol - FROZEN].weeks[0], last: periods[lastCol - FROZEN].weeks.at(-1)! });
    },
    [FROZEN, n, periods],
  );

  const [hover, setHover] = useState<{ x: number; y: number; lines: string[] } | null>(null);
  const onItemHovered = useCallback(
    (args: GridMouseEventArgs) => {
      if (args.kind !== "cell") return setHover(null);
      const [col, row] = args.location;
      const named = fixed[col]?.id === "person" && row !== n ? (row < n ? plan.assignments[row]?.personId : loadPeople[row - n - 1]) : null;
      if (named && latest.current.onOpenPerson) {
        const lines = [`Open ${latest.current.personName(named)}'s card`];
        return setHover({ x: args.bounds.x + args.bounds.width / 2, y: args.bounds.y + args.bounds.height, lines });
      }
      if (row <= n || col < FROZEN) return setHover(null);
      const person = loadPeople[row - n - 1];
      const cell = loadCells.get(person)?.[col - FROZEN];
      const weeks = load?.people[person];
      if (!cell || cell.pct === null || !weeks) return setHover(null);
      const p = periods[col - FROZEN];
      const sum = (hours: number[]) => p.weeks.reduce((s, w) => s + (hours[w] ?? 0), 0);
      const perWeek = (h: number) => `${num1.format(h / p.weeks.length)} h/wk`;
      const lines = [
        cell.pct === Infinity
          ? `${latest.current.personName(person)}, ${p.label}: no capacity on file (before their first capacity record, or after their last day)`
          : `${latest.current.personName(person)}, ${p.label}: ${pct(cell.pct, 0)} of ${perWeek(cell.capacity)}`,
        `This project: ${perWeek(cell.hours)}`,
        ...weeks.contributors.map((c) => ({ c, h: sum(c.hours) })).filter((x) => x.h > 0).map(({ c, h }) => `${c.code ? `${c.code} ` : ""}${c.name}: ${perWeek(h)}`),
      ];
      setHover({ x: args.bounds.x + args.bounds.width / 2, y: args.bounds.y + args.bounds.height, lines });
    },
    [n, FROZEN, fixed, plan.assignments, loadPeople, loadCells, load, periods],
  );

  const onCellClicked = useCallback(
    ([col, row]: Item) => {
      const id = fixed[col]?.id;
      const open = latest.current.onOpenPerson;
      const person = id === "person" && row !== n ? (row < n ? latest.current.plan.assignments[row]?.personId : loadPeople[row - n - 1]) : null;
      if (person && open) open(person);
      else if (row < n && (id === "role" || id === "person")) latest.current.onOpenRow(row);
    },
    [fixed, n, loadPeople],
  );

  const height = Math.min(620, HEADER_HEIGHT + GROUP_HEIGHT + rows * ROW_HEIGHT + 16);

  // Mock mode only: lets the end-to-end test find cells on the canvas (e2e/editor.e2e.mjs).
  const gridRef = useRef<DataEditorRef>(null);
  useEffect(() => {
    if (!MOCK) return;
    (window as unknown as { __grid?: unknown }).__grid = { frozen: FROZEN, rows: n, bounds: (c: number, r: number) => gridRef.current?.getBounds(c, r) };
  }, [FROZEN, n]);

  return (
    <div className="grid-frame" style={{ height }} onMouseLeave={() => setHover(null)}>
      <DataEditor
        ref={gridRef}
        columns={columns}
        rows={rows}
        getCellContent={getCellContent}
        onCellsEdited={editable ? onCellsEdited : undefined}
        freezeColumns={FROZEN}
        fillHandle={editable}
        allowedFillDirections="orthogonal"
        rangeSelect="rect"
        columnSelect="none"
        rowSelect="none"
        gridSelection={selection}
        onGridSelectionChange={onSelectionChange}
        getCellsForSelection={true}
        onPaste={editable}
        onItemHovered={onItemHovered}
        onCellClicked={onCellClicked}
        rowMarkers="none"
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        groupHeaderHeight={GROUP_HEIGHT}
        smoothScrollX
        smoothScrollY
        theme={theme}
        width="100%"
        height="100%"
      />
      {hover && (
        <div className="grid-tip" role="tooltip" style={{ left: hover.x, top: hover.y + 6 }}>
          {hover.lines.map((l, i) => (
            <div key={i} className={i === 0 ? "tip-head" : undefined}>
              {l}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
